import { useCallback, useState } from 'react';
import { authzClient } from '../../../lib/connect/clients';
// Validation helpers
function _isValidUrl(url) {
    try {
        // Must be relative path starting with / or valid absolute URL
        if (url.startsWith('/')) {
            return true;
        }
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch  {
        return false;
    }
}
function validatePolicyTestRequest(request) {
    // Validate payload size to prevent DoS (max 100KB)
    const payloadSize = JSON.stringify(request).length;
    const maxPayloadSize = 100 * 1024; // 100KB
    if (payloadSize > maxPayloadSize) {
        throw new Error(`Request payload exceeds maximum size of ${maxPayloadSize} bytes (${payloadSize} bytes)`);
    }
    // Validate principal
    if (!request.principal?.id || typeof request.principal.id !== 'string') {
        throw new Error('Principal ID is required and must be a string');
    }
    if (!Array.isArray(request.principal.roles) || request.principal.roles.length === 0) {
        throw new Error('Principal roles are required and must be a non-empty array');
    }
    if (!request.principal.tenant_id || typeof request.principal.tenant_id !== 'string') {
        throw new Error('Principal tenant_id is required and must be a string');
    }
    // Validate resource
    if (!request.resource?.kind || typeof request.resource.kind !== 'string') {
        throw new Error('Resource kind is required and must be a string');
    }
    if (!request.resource.id || typeof request.resource.id !== 'string') {
        throw new Error('Resource ID is required and must be a string');
    }
    if (!request.resource.tenant_id || typeof request.resource.tenant_id !== 'string') {
        throw new Error('Resource tenant_id is required and must be a string');
    }
    // Validate action
    if (!request.action || typeof request.action !== 'string') {
        throw new Error('Action is required and must be a string');
    }
    // Basic validation: reject only control characters and null bytes
    // More permissive than previous version to allow legitimate characters like quotes
    const hasControlChars = (str)=>{
        for(let i = 0; i < str.length; i++){
            const code = str.charCodeAt(i);
            // Check for control characters (0x00-0x08, 0x0B-0x0C, 0x0E-0x1F)
            if (code >= 0x00 && code <= 0x08 || code >= 0x0b && code <= 0x0c || code >= 0x0e && code <= 0x1f) {
                return true;
            }
        }
        return false;
    };
    if (hasControlChars(request.principal.id) || hasControlChars(request.resource.kind) || hasControlChars(request.resource.id) || hasControlChars(request.action)) {
        throw new Error('Request contains invalid control characters');
    }
}
export function usePolicies() {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchPolicies = useCallback(async (signal)=>{
        setLoading(true);
        setError(null);
        try {
            let policyList = [];
            const resp = await authzClient.getPolicies({}, {
                signal
            });
            policyList = (resp.policies ?? []).map((p)=>({
                    id: p.id,
                    version: p.version,
                    kind: p.kind,
                    name: p.name,
                    description: p.description || undefined,
                    updated_at: p.updatedAt
                }));
            setPolicies(policyList);
        } catch (err) {
            // Don't set error state if request was aborted
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            setError(err instanceof Error ? err.message : 'Failed to fetch policies');
        } finally{
            setLoading(false);
        }
    }, []);
    const fetchPolicyDetails = useCallback(async (policyId)=>{
        // First try to find in already-fetched policies to avoid redundant API call
        const cachedPolicy = policies.find((p)=>p.id === policyId);
        if (cachedPolicy) {
            return cachedPolicy;
        }
        // Fallback: fetch all policies if policy not in cache
        // (enies-lobby doesn't have a single policy endpoint)
        let allPolicies = [];
        const resp = await authzClient.getPolicies({});
        allPolicies = (resp.policies ?? []).map((p)=>({
                id: p.id,
                version: p.version,
                kind: p.kind,
                name: p.name,
                description: p.description || undefined,
                updated_at: p.updatedAt
            }));
        const policy = allPolicies.find((p)=>p.id === policyId);
        if (!policy) {
            throw new Error(`Policy with id "${policyId}" not found`);
        }
        return policy;
    }, [
        policies
    ]);
    const testPolicy = async (testRequest)=>{
        // Validate request before sending
        validatePolicyTestRequest(testRequest);
        const resp = await authzClient.check({
            principal: {
                id: testRequest.principal.id,
                email: testRequest.principal.email ?? '',
                roles: testRequest.principal.roles,
                tenantId: testRequest.principal.tenant_id,
                scopes: testRequest.principal.scopes ?? [],
                attr: testRequest.principal.attr ?? {}
            },
            resource: {
                kind: testRequest.resource.kind,
                id: testRequest.resource.id,
                tenantId: testRequest.resource.tenant_id,
                attr: testRequest.resource.attr ?? {}
            },
            action: testRequest.action
        });
        return {
            allowed: resp.allowed,
            policyId: resp.policyId || undefined,
            message: resp.validationErrors.length > 0 ? resp.validationErrors.join('; ') : undefined
        };
    };
    const validatePolicy = async (yaml)=>{
        if (!yaml || typeof yaml !== 'string' || yaml.trim().length === 0) {
            throw new Error('Policy YAML is required');
        }
        const resp = await authzClient.validatePolicy({
            yaml
        });
        return {
            valid: resp.valid,
            errors: resp.errors.length > 0 ? [
                ...resp.errors
            ] : undefined,
            warnings: resp.warnings.length > 0 ? [
                ...resp.warnings
            ] : undefined,
            policy_id: resp.policyId || undefined,
            resource_kind: resp.resourceKind || undefined,
            message: resp.message || undefined,
            request_id: resp.requestId
        };
    };
    return {
        policies,
        loading,
        error,
        fetchPolicies,
        fetchPolicyDetails,
        testPolicy,
        validatePolicy
    };
}
